<template>
  <section :class="{ 'single-source-content': reviewMode }">
    <div v-if="reviewMode" class="source-heading">
      <span>{{ copy.heading }}</span><a-tag color="blue">{{ copy.local }}</a-tag>
    </div>
    <p v-if="reviewMode" class="source-tip">{{ copy.tip }}</p>
    <a-form-model-item v-for="field in fields" :key="field" :label="label(field)" :required="field === fields[0]">
      <a-textarea v-if="field === 'description'" :value="value.originalContent[field]" :rows="3" :disabled="busy"
        @input="setOriginal(field, $event)" />
      <a-input v-else :value="value.originalContent[field]" :disabled="busy" @input="setOriginal(field, $event)" />
    </a-form-model-item>
    <div v-if="reviewMode" class="source-status" role="status">
      <a-icon :type="busy ? 'loading' : 'translation'" />
      {{ busy ? copy.busy : value.translationMeta ? copy.saved : copy.onSave }}
    </div>
    <a-collapse v-if="reviewMode" :default-active-key="['translations']" :bordered="false" class="translation-details">
      <a-collapse-panel key="translations" :header="copy.advanced">
        <div class="source-detection">
          <label>{{ copy.detect }}</label>
          <a-select :value="value.sourceLocaleHint" :disabled="busy" @change="$set(value, 'sourceLocaleHint', $event)">
            <a-select-option value="auto">{{ copy.auto }}</a-select-option>
            <a-select-option v-for="locale in locales" :key="locale" :value="locale">{{ localeLabel(locale) }}</a-select-option>
          </a-select>
          <a-button :loading="busy" @click="preview">{{ copy.preview }}</a-button>
        </div>
        <p class="source-tip">{{ copy.reviewTip }}</p>
        <a-alert v-if="needsReview" type="warning" show-icon :message="copy.stale" />
        <a-tabs>
          <a-tab-pane v-for="locale in locales" :key="locale" :tab="localeLabel(locale)">
            <a-form-model-item v-for="field in fields" :key="field" :label="label(field)">
              <a-textarea :value="value[fieldKey(field, locale)]" :rows="field === 'description' ? 3 : 1" :disabled="busy"
                @input="$set(value, fieldKey(field, locale), inputText($event))" />
            </a-form-model-item>
          </a-tab-pane>
        </a-tabs>
        <a-button v-if="needsReview" @click="confirmReview">{{ copy.confirm }}</a-button>
      </a-collapse-panel>
    </a-collapse>
  </section>
</template>

<script>
import request from '@/utils/request'
import { LANGUAGES, fieldName, pendingContent, mergeContentTranslation, contentNeedsReview, confirmContentReview, validateContentLengths, deferContentTranslation } from '@/utils/single-source-content'

const messages = {
  'zh-Hans': {
    heading: '只需填写一份原文', local: '本地翻译',
    tip: '直接使用简体、繁体或英文填写。保存时自动准备三语内容，无需逐个切换语言。',
    onSave: '保存时生成译文', busy: '正在本机翻译，请稍候…', saved: '原文和译文已保留；机器翻译仍建议校对',
    advanced: '译文预览与人工校对', detect: '原文识别', auto: '自动识别', preview: '生成预览',
    reviewTip: '已有人工译文不会被覆盖。赛事名称、地点及重要说明请核对；修改原文后，旧人工译文需要重新确认。',
    stale: '有待核对的人工译文、术语或数字，请检查后确认。', confirm: '已核对，保留当前译文',
    required: '请填写标题或组别名称', changed: '翻译期间内容已变化，请重新生成', reviewRequired: '请展开译文预览，核对待确认内容后再保存'
  },
  'zh-Hant': {
    heading: '只需填寫一份原文', local: '本地翻譯',
    tip: '直接使用簡體、繁體或英文填寫。儲存時自動準備三語內容，無需逐個切換語言。',
    onSave: '儲存時產生譯文', busy: '正在本機翻譯，請稍候…', saved: '原文和譯文已保留；機器翻譯仍建議校對',
    advanced: '譯文預覽與人工校對', detect: '原文識別', auto: '自動識別', preview: '產生預覽',
    reviewTip: '已有人工譯文不會被覆蓋。賽事名稱、地點及重要說明請核對；修改原文後，舊人工譯文需要重新確認。',
    stale: '有待核對的人工譯文、術語或數字，請檢查後確認。', confirm: '已核對，保留目前譯文',
    required: '請填寫標題或組別名稱', changed: '翻譯期間內容已變化，請重新產生', reviewRequired: '請展開譯文預覽，核對待確認內容後再儲存'
  },
  en: {
    heading: 'Enter your content once', local: 'Offline translation',
    tip: 'Write in Simplified Chinese, Traditional Chinese or English. Saving prepares all three languages automatically.',
    onSave: 'Translations will be prepared on save', busy: 'Translating on this computer…', saved: 'Original and translations retained; please review machine drafts',
    advanced: 'Translation preview and corrections', detect: 'Source language', auto: 'Detect automatically', preview: 'Generate preview',
    reviewTip: 'Existing human translations are protected. Review official names, venues and important notices. Changed originals require a fresh review of human translations.',
    stale: 'Some human translations, terms or numbers need review.', confirm: 'Reviewed — keep these translations',
    required: 'Enter an event or category name', changed: 'Content changed during translation. Please try again.', reviewRequired: 'Open the translation preview and review flagged content before saving.'
  }
}

export default {
  props: { value: { type: Object, required: true }, fields: { type: Array, required: true }, kind: { type: String, required: true }, eventId: { default: null }, reviewMode: Boolean },
  data: () => ({ busy: false, locales: LANGUAGES }),
  computed: {
    copy() { return messages[this.$adminLocale] || messages['zh-Hans'] },
    needsReview() { return contentNeedsReview(this.value) }
  },
  methods: {
    fieldKey: fieldName,
    label(field) { return this.$t(({ title: 'eventTitle', subtitle: 'eventSubtitle', eventType: 'eventType', location: 'eventLocation', description: this.kind === 'round' ? 'roundDescription' : this.kind === 'category' ? 'categoryDescription' : 'eventDescription', categoryName: 'categoryName', stageName: 'stageName', roundName: 'roundName' })[field]) },
    localeLabel(locale) { return locale === 'en' ? 'English' : locale === 'zh-Hant' ? '繁體中文' : '简体中文' },
    inputText(input) {
      const value = input && input.target ? input.target.value : input
      return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
    },
    setOriginal(field, input) { this.$set(this.value.originalContent, field, this.inputText(input)) },
    confirmReview() { confirmContentReview(this.value) },
    async preview() {
      try { await this.prepare(false, true) } catch (error) { this.$message.warning(error.message) }
    },
    async prepare(requireReview = this.reviewMode, force = false) {
      if (this.busy) throw new Error(this.copy.busy)
      if (!String(this.value.originalContent[this.fields[0]] || '').trim()) throw new Error(this.kind === 'stage' ? this.$t('stageNameRequired') : this.copy.required)
      const snapshot = JSON.stringify(this.value)
      const texts = pendingContent(this.value, this.fields, force)
      this.busy = true
      try {
        let generated = {}
        if (Object.keys(texts).length) {
          try {
            const response = await request.post('/dragonboat/event/content-translation', {
            eventId: this.eventId, kind: this.kind, sourceLocale: this.value.sourceLocaleHint, texts
            }, { timeout: 50000, silentError: !this.reviewMode })
            generated = response.data.fields
          } catch (error) {
            if (this.reviewMode || [401, 403].includes(Number(error.businessCode)) || (error.response && [401, 403].includes(error.response.status))) throw error
            if (JSON.stringify(this.value) !== snapshot) throw new Error(this.copy.changed)
            const deferred = deferContentTranslation(this.value, this.fields, texts)
            validateContentLengths(deferred, this.fields, this.kind)
            Object.keys(deferred).forEach(key => this.$set(this.value, key, deferred[key]))
            return this.value
          }
        }
        if (JSON.stringify(this.value) !== snapshot) throw new Error(this.copy.changed)
        let translated = mergeContentTranslation(this.value, this.fields, generated)
        if (!this.reviewMode) translated = deferContentTranslation(translated, this.fields)
        Object.keys(translated).forEach(key => this.$set(this.value, key, translated[key]))
        validateContentLengths(this.value, this.fields, this.kind)
        if (requireReview && contentNeedsReview(this.value)) throw new Error(this.copy.reviewRequired)
        this.$set(this.value.translationMeta, 'pending', contentNeedsReview(this.value))
        return this.value
      } finally { this.busy = false }
    }
  }
}
</script>

<style scoped>
.single-source-content { margin-bottom: 22px; padding: 18px 16px 8px; border: 1px solid #e7ebf0; border-radius: 10px; background: #fafcfd; }
.source-heading { display: flex; align-items: center; gap: 10px; font-weight: 600; color: #24364b; }
.source-heading .ant-tag { margin: 0; font-weight: 400; }
.source-tip { font-size: 12px; line-height: 1.7; color: #68788c; margin: 10px 0 16px; }
.source-status { display: flex; align-items: center; gap: 7px; color: #68788c; font-size: 12px; margin-bottom: 12px; }
.translation-details { background: transparent; }
.source-detection { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.source-detection .ant-select { min-width: 145px; }
</style>
